import "./styles.css";

const API_PREFIX = "/api";
const SESSION_KEY = "video-worker-upload-v1";
const PART_BATCH_SIZE = 6;
const PART_CONCURRENCY = 3;
const MAX_UPLOAD_ATTEMPTS = 4;

type JobStatus = "pending" | "claimed" | "processing" | "uploading" | "done" | "failed";

type Job = {
  id: string;
  title: string;
  script_text: string | null;
  status: JobStatus;
  input_keys: string;
  output_key: string | null;
  thumbnail_key: string | null;
  error: string | null;
  retry_count: number;
  worker_heartbeat_at: number | null;
  created_at: number;
  updated_at: number;
};

type UploadPart = {
  part_number: number;
  etag: string;
};

type UploadTarget = {
  file_index: number;
  key: string;
  filename: string;
  content_type: string;
  size: number;
  upload_id: string;
  part_size: number;
  part_count: number;
  parts: UploadPart[];
  completed: boolean;
};

type StoredUploadSession = {
  version: 1;
  job_id: string;
  title: string;
  created_at: number;
  uploads: UploadTarget[];
};

type CreateJobResponse = {
  job_id: string;
  status: "uploading";
  uploads: Omit<UploadTarget, "parts" | "completed">[];
};

type PresignedPart = {
  part_number: number;
  url: string;
};

class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing app root");

app.innerHTML = `
  <div class="ambient ambient-one"></div>
  <div class="ambient ambient-two"></div>
  <div class="offline-banner" id="offlineBanner" hidden>ออฟไลน์อยู่ — การอัปโหลดจะไปต่อเมื่อเน็ตกลับมา</div>

  <section class="login-shell" id="loginView" hidden>
    <div class="brand-mark" aria-hidden="true"><span></span></div>
    <p class="eyebrow">PRIVATE EDIT PIPELINE</p>
    <h1>ส่งคลิปเข้า<br />คิวตัดต่อ</h1>
    <p class="lede">พื้นที่ส่วนตัวสำหรับส่งไฟล์จาก iPhone ไปยัง Video Worker</p>
    <form class="login-card" id="loginForm">
      <label for="passcode">รหัสผ่าน</label>
      <div class="passcode-row">
        <input id="passcode" name="passcode" type="password" autocomplete="current-password" required />
        <button type="submit" class="button button-primary">เข้าใช้งาน</button>
      </div>
      <p class="form-error" id="loginError" role="alert"></p>
    </form>
  </section>

  <main id="mainView" hidden>
    <header class="topbar">
      <a class="wordmark" href="/" aria-label="Video Worker หน้าแรก">
        <span class="wordmark-icon"><i></i></span>
        <span>VIDEO<br />WORKER</span>
      </a>
      <div class="topbar-actions">
        <div class="worker-indicator" id="workerIndicator">
          <span class="status-dot"></span>
          <span id="workerText">กำลังตรวจสอบ Worker</span>
        </div>
        <button class="icon-button" id="logoutButton" type="button" aria-label="ออกจากระบบ" title="ออกจากระบบ">↗</button>
      </div>
    </header>

    <div class="layout">
      <section class="hero-panel">
        <div>
          <p class="eyebrow">NEW EDIT</p>
          <h1>โยนไฟล์มา<br /><em>เดี๋ยวจัดให้</em></h1>
          <p class="lede">เลือกวิดีโอ เสียง หรือภาพ แล้วปล่อยให้ระบบอัปโหลดต่อได้แม้สัญญาณสะดุด</p>
        </div>

        <form id="jobForm" class="job-form">
          <label class="field">
            <span>ชื่องาน</span>
            <input id="title" name="title" maxlength="200" placeholder="เช่น ทริปหัวหิน 30 วิ" required />
          </label>

          <label class="field">
            <span>บรีฟตัดต่อ <small>ไม่บังคับ</small></span>
            <textarea id="scriptText" name="script_text" maxlength="20000" rows="4" placeholder="โทนที่อยากได้, ลำดับคลิป, เพลง, ข้อความบนจอ..."></textarea>
          </label>

          <label class="drop-zone" id="dropZone" for="files">
            <input id="files" name="files" type="file" accept="video/*,audio/*,image/*,.mov,.mp4,.m4v,.hevc" multiple />
            <span class="drop-plus">＋</span>
            <strong>เลือกไฟล์</strong>
            <small>วิดีโอ · เสียง · ภาพ / สูงสุด 20 ไฟล์</small>
          </label>
          <div class="file-list" id="selectedFiles"></div>

          <button class="button button-primary button-wide" id="submitJob" type="submit">
            <span>ส่งเข้าคิว</span><span aria-hidden="true">→</span>
          </button>
          <p class="form-error" id="jobError" role="alert"></p>
        </form>

        <section class="upload-panel" id="uploadPanel" hidden aria-live="polite">
          <div class="upload-heading">
            <div>
              <p class="eyebrow">UPLOADING</p>
              <h2 id="uploadTitle">กำลังส่งไฟล์</h2>
            </div>
            <strong id="uploadPercent">0%</strong>
          </div>
          <div class="progress-track"><span id="uploadProgress"></span></div>
          <p id="uploadDetail">กำลังเตรียมไฟล์…</p>
          <button class="text-button danger" id="cancelUpload" type="button">ยกเลิกการอัปโหลด</button>
        </section>

        <section class="resume-panel" id="resumePanel" hidden>
          <p class="eyebrow">RESUME UPLOAD</p>
          <h2>มีงานที่ส่งค้างไว้</h2>
          <p id="resumeDescription"></p>
          <label class="button button-secondary button-wide" for="resumeFiles">เลือกไฟล์เดิมเพื่อส่งต่อ</label>
          <input id="resumeFiles" class="visually-hidden" type="file" accept="video/*,audio/*,image/*" multiple />
          <button class="text-button danger" id="discardUpload" type="button">ยกเลิกงานนี้</button>
          <p class="form-error" id="resumeError" role="alert"></p>
        </section>
      </section>

      <section class="queue-panel">
        <div class="section-heading">
          <div>
            <p class="eyebrow">EDIT QUEUE</p>
            <h2>งานล่าสุด</h2>
          </div>
          <button class="icon-button refresh-button" id="refreshJobs" type="button" aria-label="รีเฟรชรายการ">↻</button>
        </div>
        <div class="job-list" id="jobList">
          <div class="empty-state">กำลังโหลดคิว…</div>
        </div>
      </section>
    </div>
  </main>
`;

function requiredElement<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing element: ${selector}`);
  return element;
}

const loginView = requiredElement<HTMLElement>("#loginView");
const mainView = requiredElement<HTMLElement>("#mainView");
const loginForm = requiredElement<HTMLFormElement>("#loginForm");
const passcodeInput = requiredElement<HTMLInputElement>("#passcode");
const loginError = requiredElement<HTMLParagraphElement>("#loginError");
const jobForm = requiredElement<HTMLFormElement>("#jobForm");
const titleInput = requiredElement<HTMLInputElement>("#title");
const scriptInput = requiredElement<HTMLTextAreaElement>("#scriptText");
const filesInput = requiredElement<HTMLInputElement>("#files");
const selectedFiles = requiredElement<HTMLDivElement>("#selectedFiles");
const jobError = requiredElement<HTMLParagraphElement>("#jobError");
const submitJobButton = requiredElement<HTMLButtonElement>("#submitJob");
const uploadPanel = requiredElement<HTMLElement>("#uploadPanel");
const uploadTitle = requiredElement<HTMLHeadingElement>("#uploadTitle");
const uploadPercent = requiredElement<HTMLElement>("#uploadPercent");
const uploadProgress = requiredElement<HTMLElement>("#uploadProgress");
const uploadDetail = requiredElement<HTMLParagraphElement>("#uploadDetail");
const cancelUploadButton = requiredElement<HTMLButtonElement>("#cancelUpload");
const resumePanel = requiredElement<HTMLElement>("#resumePanel");
const resumeDescription = requiredElement<HTMLParagraphElement>("#resumeDescription");
const resumeFilesInput = requiredElement<HTMLInputElement>("#resumeFiles");
const resumeError = requiredElement<HTMLParagraphElement>("#resumeError");
const discardUploadButton = requiredElement<HTMLButtonElement>("#discardUpload");
const jobList = requiredElement<HTMLDivElement>("#jobList");
const workerIndicator = requiredElement<HTMLDivElement>("#workerIndicator");
const workerText = requiredElement<HTMLSpanElement>("#workerText");
const offlineBanner = requiredElement<HTMLDivElement>("#offlineBanner");

let authenticated = false;
let currentJobs: Job[] = [];
let uploadAbortController: AbortController | null = null;
let uploadRunning = false;
let selectedJobFiles: File[] = [];

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("content-type")) headers.set("content-type", "application/json");

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...init,
    headers,
    credentials: "same-origin",
  });
  const body = await response.json().catch(() => null) as { error?: unknown } | null;
  if (!response.ok) {
    const message = typeof body?.error === "string" ? body.error : `request failed (${response.status})`;
    throw new ApiError(response.status, message);
  }
  return body as T;
}

function formatBytes(value: number): string {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let size = value / 1024;
  let unit = units[0];
  for (let index = 1; size >= 1024 && index < units.length; index += 1) {
    size /= 1024;
    unit = units[index];
  }
  return `${size >= 10 ? size.toFixed(0) : size.toFixed(1)} ${unit}`;
}

function formatDate(value: number): string {
  return new Intl.DateTimeFormat("th-TH", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

function messageFromError(error: unknown): string {
  if (error instanceof DOMException && error.name === "AbortError") return "ยกเลิกการอัปโหลดแล้ว";
  if (error instanceof ApiError && error.status === 401) return "เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่";
  if (error instanceof Error) return error.message;
  return "เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ";
}

function isStoredSession(value: unknown): value is StoredUploadSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StoredUploadSession>;
  return (
    candidate.version === 1 &&
    typeof candidate.job_id === "string" &&
    typeof candidate.title === "string" &&
    typeof candidate.created_at === "number" &&
    Array.isArray(candidate.uploads) &&
    candidate.uploads.every((upload) =>
      upload &&
      typeof upload.file_index === "number" &&
      typeof upload.filename === "string" &&
      typeof upload.size === "number" &&
      typeof upload.upload_id === "string" &&
      typeof upload.part_size === "number" &&
      typeof upload.part_count === "number" &&
      typeof upload.completed === "boolean" &&
      Array.isArray(upload.parts),
    )
  );
}

function loadUploadSession(): StoredUploadSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (!isStoredSession(value)) throw new Error("invalid stored upload");
    return value;
  } catch {
    localStorage.removeItem(SESSION_KEY);
    return null;
  }
}

function saveUploadSession(session: StoredUploadSession): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  renderResumePanel();
}

function clearUploadSession(): void {
  localStorage.removeItem(SESSION_KEY);
  renderResumePanel();
}

function renderSelectedFiles(): void {
  selectedFiles.replaceChildren();
  for (const [index, file] of selectedJobFiles.entries()) {
    const row = document.createElement("div");
    row.className = "file-row";
    const details = document.createElement("div");
    details.className = "file-details";
    const name = document.createElement("span");
    name.textContent = file.name;
    const size = document.createElement("small");
    size.textContent = formatBytes(file.size);
    const remove = document.createElement("button");
    remove.className = "file-remove";
    remove.type = "button";
    remove.textContent = "ลบ";
    remove.setAttribute("aria-label", `ลบไฟล์ ${file.name}`);
    remove.addEventListener("click", () => {
      selectedJobFiles.splice(index, 1);
      jobError.textContent = "";
      renderSelectedFiles();
    });
    details.append(name, size);
    row.append(details, remove);
    selectedFiles.append(row);
  }
}

function addSelectedFiles(files: File[]): void {
  const existing = new Set(
    selectedJobFiles.map((file) => `${file.name}\u0000${file.size}\u0000${file.lastModified}`),
  );
  for (const file of files) {
    const identity = `${file.name}\u0000${file.size}\u0000${file.lastModified}`;
    if (!existing.has(identity)) {
      selectedJobFiles.push(file);
      existing.add(identity);
    }
  }
  if (selectedJobFiles.length > 20) {
    selectedJobFiles = selectedJobFiles.slice(0, 20);
    jobError.textContent = "เลือกได้สูงสุด 20 ไฟล์ ไฟล์ที่เกินยังไม่ได้ถูกเพิ่ม";
  }
  renderSelectedFiles();
}

function renderResumePanel(): void {
  const session = loadUploadSession();
  resumePanel.hidden = !session || uploadRunning;
  jobForm.hidden = Boolean(session) || uploadRunning;
  if (!session) return;
  const completed = session.uploads.filter((upload) => upload.completed).length;
  resumeDescription.textContent = `${session.title} · ส่งแล้ว ${completed}/${session.uploads.length} ไฟล์ — เลือกไฟล์ชุดเดิมเพื่อทำต่อ`;
}

function completedBytes(session: StoredUploadSession): number {
  return session.uploads.reduce((total, upload) => {
    if (upload.completed) return total + upload.size;
    const uploaded = upload.parts.reduce((partTotal, part) => {
      const start = (part.part_number - 1) * upload.part_size;
      return partTotal + Math.min(upload.part_size, Math.max(0, upload.size - start));
    }, 0);
    return total + uploaded;
  }, 0);
}

function updateUploadProgress(session: StoredUploadSession, detail: string): void {
  const total = session.uploads.reduce((sum, upload) => sum + upload.size, 0);
  const sent = completedBytes(session);
  const percent = total === 0 ? 0 : Math.min(100, Math.round((sent / total) * 100));
  uploadPercent.textContent = `${percent}%`;
  uploadProgress.style.width = `${percent}%`;
  uploadDetail.textContent = `${detail} · ${formatBytes(sent)} / ${formatBytes(total)}`;
}

async function requestPartUrls(
  session: StoredUploadSession,
  upload: UploadTarget,
  partNumbers: number[],
  signal: AbortSignal,
): Promise<PresignedPart[]> {
  const response = await api<{ parts: PresignedPart[] }>(
    `/jobs/${encodeURIComponent(session.job_id)}/uploads/${upload.file_index}/parts`,
    {
      method: "POST",
      body: JSON.stringify({ upload_id: upload.upload_id, part_numbers: partNumbers }),
      signal,
    },
  );
  return response.parts;
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, ms);
    signal.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timer);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });
}

async function putPart(
  session: StoredUploadSession,
  upload: UploadTarget,
  file: File,
  partNumber: number,
  initialUrl: string,
  signal: AbortSignal,
): Promise<UploadPart> {
  const start = (partNumber - 1) * upload.part_size;
  const body = file.slice(start, Math.min(start + upload.part_size, file.size));
  let url = initialUrl;
  let lastError: unknown;

  for (let attempt = 0; attempt < MAX_UPLOAD_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await sleep(500 * 2 ** (attempt - 1) + Math.random() * 250, signal);
      const refreshed = await requestPartUrls(session, upload, [partNumber], signal);
      const part = refreshed[0];
      if (!part) throw new Error("ไม่ได้รับ URL สำหรับอัปโหลดส่วนไฟล์");
      url = part.url;
    }

    try {
      const response = await fetch(url, { method: "PUT", body, signal });
      if (!response.ok) throw new Error(`R2 ตอบกลับ ${response.status}`);
      const etag = response.headers.get("etag")?.replace(/^"|"$/g, "");
      if (!etag || !/^[a-fA-F0-9]{32}$/.test(etag)) {
        throw new Error("อ่าน ETag ไม่ได้ — ตรวจสอบ R2 CORS exposeHeaders");
      }
      return { part_number: partNumber, etag };
    } catch (error) {
      if (signal.aborted) throw error;
      lastError = error;
    }
  }

  throw lastError instanceof Error ? lastError : new Error("อัปโหลดส่วนไฟล์ไม่สำเร็จ");
}

async function mapConcurrent<T>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<void>,
): Promise<void> {
  let cursor = 0;
  async function worker(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      const item = items[index];
      if (item !== undefined) await task(item);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, () => worker()));
}

async function uploadOneFile(
  session: StoredUploadSession,
  upload: UploadTarget,
  file: File,
  signal: AbortSignal,
): Promise<void> {
  if (upload.completed) return;
  const completedParts = new Set(upload.parts.map((part) => part.part_number));
  const pending = Array.from(
    { length: upload.part_count },
    (_, index) => index + 1,
  ).filter((partNumber) => !completedParts.has(partNumber));

  for (let offset = 0; offset < pending.length; offset += PART_BATCH_SIZE) {
    const batch = pending.slice(offset, offset + PART_BATCH_SIZE);
    const urls = await requestPartUrls(session, upload, batch, signal);
    const urlsByPart = new Map(urls.map((part) => [part.part_number, part.url]));

    await mapConcurrent(batch, PART_CONCURRENCY, async (partNumber) => {
      const url = urlsByPart.get(partNumber);
      if (!url) throw new Error(`ไม่ได้รับ URL ของ part ${partNumber}`);
      const uploadedPart = await putPart(session, upload, file, partNumber, url, signal);
      upload.parts = upload.parts.filter((part) => part.part_number !== partNumber);
      upload.parts.push(uploadedPart);
      upload.parts.sort((a, b) => a.part_number - b.part_number);
      saveUploadSession(session);
      updateUploadProgress(session, `กำลังส่ง ${upload.filename}`);
    });
  }

  await api<{ status: JobStatus }>(
    `/jobs/${encodeURIComponent(session.job_id)}/uploads/${upload.file_index}/complete`,
    {
      method: "POST",
      body: JSON.stringify({ upload_id: upload.upload_id, parts: upload.parts }),
      signal,
    },
  );
  upload.completed = true;
  saveUploadSession(session);
  updateUploadProgress(session, `ส่ง ${upload.filename} เรียบร้อย`);
}

function validateResumeFiles(session: StoredUploadSession, files: File[]): string | null {
  if (files.length !== session.uploads.length) {
    return `กรุณาเลือกไฟล์เดิมให้ครบ ${session.uploads.length} ไฟล์`;
  }
  for (const upload of session.uploads) {
    const file = files[upload.file_index];
    if (!file || file.name !== upload.filename || file.size !== upload.size) {
      return `ไฟล์ลำดับ ${upload.file_index + 1} ต้องเป็น ${upload.filename} (${formatBytes(upload.size)})`;
    }
  }
  return null;
}

async function runUpload(session: StoredUploadSession, files: File[]): Promise<void> {
  if (uploadRunning) return;
  const validationError = validateResumeFiles(session, files);
  if (validationError) {
    resumeError.textContent = validationError;
    return;
  }

  uploadRunning = true;
  uploadAbortController = new AbortController();
  uploadTitle.textContent = session.title;
  uploadPanel.hidden = false;
  resumePanel.hidden = true;
  jobForm.hidden = true;
  updateUploadProgress(session, "กำลังเตรียมอัปโหลด");

  try {
    for (const upload of session.uploads) {
      const file = files[upload.file_index];
      if (!file) throw new Error(`ไม่พบไฟล์ ${upload.filename}`);
      await uploadOneFile(session, upload, file, uploadAbortController.signal);
    }
    clearUploadSession();
    uploadDetail.textContent = "ส่งไฟล์ครบแล้ว งานเข้าคิวตัดต่อเรียบร้อย";
    uploadPercent.textContent = "100%";
    uploadProgress.style.width = "100%";
    await loadJobs();
    window.setTimeout(() => {
      uploadPanel.hidden = true;
      jobForm.hidden = false;
    }, 1800);
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      const response = await api<{ job: Job }>(`/jobs/${encodeURIComponent(session.job_id)}`).catch(() => null);
      if (response?.job.status === "pending") {
        clearUploadSession();
        uploadDetail.textContent = "ส่งไฟล์ครบแล้ว งานเข้าคิวตัดต่อเรียบร้อย";
        await loadJobs();
        window.setTimeout(() => {
          uploadPanel.hidden = true;
          jobForm.hidden = false;
        }, 1800);
        return;
      }
    }
    const errorMessage = `${messageFromError(error)} — เลือกไฟล์เดิมเพื่อส่งต่อได้`;
    uploadDetail.textContent = errorMessage;
    resumeError.textContent = errorMessage;
    uploadPanel.hidden = true;
  } finally {
    uploadRunning = false;
    uploadAbortController = null;
    cancelUploadButton.disabled = false;
    renderResumePanel();
    if (!uploadPanel.hidden) jobForm.hidden = true;
  }
}

async function cancelStoredUpload(): Promise<void> {
  const session = loadUploadSession();
  if (!session) return;
  uploadAbortController?.abort();
  discardUploadButton.disabled = true;
  cancelUploadButton.disabled = true;
  try {
    await api(
      `/jobs/${encodeURIComponent(session.job_id)}/uploads/abort`,
      {
        method: "POST",
        body: JSON.stringify({
          uploads: session.uploads.map((upload) => ({
            file_index: upload.file_index,
            upload_id: upload.upload_id,
          })),
        }),
      },
    );
    clearUploadSession();
    uploadPanel.hidden = true;
    await loadJobs();
  } catch (error) {
    resumeError.textContent = messageFromError(error);
  } finally {
    discardUploadButton.disabled = false;
    cancelUploadButton.disabled = false;
    renderResumePanel();
  }
}

const statusLabels: Record<JobStatus, string> = {
  uploading: "กำลังรับไฟล์",
  pending: "รอคิว",
  claimed: "Worker รับงานแล้ว",
  processing: "กำลังตัดต่อ",
  done: "เสร็จแล้ว",
  failed: "ไม่สำเร็จ",
};

function inputCount(job: Job): number {
  try {
    const value: unknown = JSON.parse(job.input_keys);
    return Array.isArray(value) ? value.length : 0;
  } catch {
    return 0;
  }
}

function renderJobs(): void {
  jobList.replaceChildren();
  if (currentJobs.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.textContent = "ยังไม่มีงาน ส่งไฟล์ชุดแรกได้เลย";
    jobList.append(empty);
    renderWorkerStatus();
    return;
  }

  for (const job of currentJobs) {
    const card = document.createElement("article");
    card.className = `job-card status-${job.status}`;

    const top = document.createElement("div");
    top.className = "job-card-top";
    const title = document.createElement("h3");
    title.textContent = job.title;
    const badge = document.createElement("span");
    badge.className = "status-badge";
    badge.textContent = statusLabels[job.status];
    top.append(title, badge);

    const meta = document.createElement("p");
    meta.className = "job-meta";
    meta.textContent = `${formatDate(job.created_at)} · ${inputCount(job)} ไฟล์`;
    if (job.status === "done" && job.thumbnail_key) {
      const thumbnail = document.createElement("img");
      thumbnail.className = "job-thumbnail";
      thumbnail.src = `${API_PREFIX}/jobs/${encodeURIComponent(job.id)}/thumbnail`;
      thumbnail.alt = `ภาพตัวอย่าง ${job.title}`;
      thumbnail.loading = "lazy";
      card.append(thumbnail);
    }
    card.append(top, meta);

    if (job.error) {
      const error = document.createElement("p");
      error.className = "job-error";
      error.textContent = job.error;
      card.append(error);
    }

    if (job.status === "done") {
      const button = document.createElement("button");
      button.className = "button button-secondary download-button";
      button.type = "button";
      button.textContent = "ดาวน์โหลดไฟล์ →";
      button.addEventListener("click", async () => {
        button.disabled = true;
        try {
          const response = await api<{ url: string }>(`/jobs/${encodeURIComponent(job.id)}/download`);
          window.location.assign(response.url);
        } catch (error) {
          button.textContent = messageFromError(error);
          button.disabled = false;
        }
      });
      card.append(button);
    }

    if (job.status === "pending" || job.status === "failed") {
      const deleteButton = document.createElement("button");
      deleteButton.className = "text-button danger delete-job-button";
      deleteButton.type = "button";
      deleteButton.textContent = "ลบงานและไฟล์ที่อัปโหลด";
      deleteButton.addEventListener("click", async () => {
        if (!window.confirm(`ลบงาน “${job.title}” และไฟล์ทั้งหมดที่อัปโหลดใช่ไหม?`)) return;
        deleteButton.disabled = true;
        try {
          await api(`/jobs/${encodeURIComponent(job.id)}`, { method: "DELETE" });
          currentJobs = currentJobs.filter((item) => item.id !== job.id);
          renderJobs();
        } catch (error) {
          deleteButton.textContent = messageFromError(error);
          deleteButton.disabled = false;
          await loadJobs();
        }
      });
      card.append(deleteButton);
    }

    jobList.append(card);
  }
  renderWorkerStatus();
}

function renderWorkerStatus(): void {
  const latestHeartbeat = currentJobs.reduce<number | null>((latest, job) => {
    if (!job.worker_heartbeat_at) return latest;
    return latest === null ? job.worker_heartbeat_at : Math.max(latest, job.worker_heartbeat_at);
  }, null);
  const online = latestHeartbeat !== null && Date.now() - latestHeartbeat < 3 * 60 * 1000;
  workerIndicator.classList.toggle("online", online);
  workerIndicator.classList.toggle("offline", latestHeartbeat !== null && !online);
  workerText.textContent = online
    ? "Worker ออนไลน์"
    : latestHeartbeat === null
      ? "ยังไม่พบ Worker"
      : "Worker ออฟไลน์";
}

async function loadJobs(): Promise<void> {
  try {
    const response = await api<{ jobs: Job[] }>("/jobs");
    authenticated = true;
    currentJobs = response.jobs;
    loginView.hidden = true;
    mainView.hidden = false;
    renderJobs();
    renderResumePanel();
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      authenticated = false;
      mainView.hidden = true;
      loginView.hidden = false;
      return;
    }
    jobList.innerHTML = `<div class="empty-state">โหลดคิวไม่สำเร็จ กรุณาลองอีกครั้ง</div>`;
  }
}

loginForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  loginError.textContent = "";
  const button = loginForm.querySelector<HTMLButtonElement>("button[type=submit]");
  if (!button) return;
  button.disabled = true;
  try {
    await api("/auth", {
      method: "POST",
      body: JSON.stringify({ passcode: passcodeInput.value }),
    });
    passcodeInput.value = "";
    await loadJobs();
  } catch (error) {
    loginError.textContent = error instanceof ApiError && error.status === 401
      ? "รหัสผ่านไม่ถูกต้อง"
      : messageFromError(error);
  } finally {
    button.disabled = false;
  }
});

jobForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  jobError.textContent = "";
  const files = [...selectedJobFiles];
  if (files.length === 0 || files.length > 20) {
    jobError.textContent = "กรุณาเลือก 1–20 ไฟล์";
    return;
  }
  if (files.some((file) => file.size === 0)) {
    jobError.textContent = "มีไฟล์ขนาด 0 byte กรุณานำไฟล์นั้นออก";
    return;
  }
  if (loadUploadSession()) {
    jobError.textContent = "มีงานอัปโหลดค้างอยู่ กรุณาส่งต่อหรือยกเลิกงานเดิมก่อน";
    return;
  }

  submitJobButton.disabled = true;
  try {
    const response = await api<CreateJobResponse>("/jobs", {
      method: "POST",
      body: JSON.stringify({
        title: titleInput.value,
        script_text: scriptInput.value,
        files: files.map((file) => ({
          filename: file.name,
          size: file.size,
          content_type: file.type || "application/octet-stream",
        })),
      }),
    });
    const session: StoredUploadSession = {
      version: 1,
      job_id: response.job_id,
      title: titleInput.value.trim(),
      created_at: Date.now(),
      uploads: response.uploads.map((upload) => ({ ...upload, parts: [], completed: false })),
    };
    saveUploadSession(session);
    await runUpload(session, files);
    if (!loadUploadSession()) {
      jobForm.reset();
      selectedJobFiles = [];
      renderSelectedFiles();
    }
  } catch (error) {
    jobError.textContent = messageFromError(error);
  } finally {
    submitJobButton.disabled = false;
  }
});

filesInput.addEventListener("change", () => {
  jobError.textContent = "";
  addSelectedFiles(Array.from(filesInput.files ?? []));
  // Keep our own File references so iOS can re-open the picker and users can
  // remove individual files without relying on the unsupported DataTransfer API.
  filesInput.value = "";
});
resumeFilesInput.addEventListener("change", () => {
  resumeError.textContent = "";
  const session = loadUploadSession();
  if (session) void runUpload(session, Array.from(resumeFilesInput.files ?? []));
  resumeFilesInput.value = "";
});
cancelUploadButton.addEventListener("click", () => void cancelStoredUpload());
discardUploadButton.addEventListener("click", () => void cancelStoredUpload());
requiredElement<HTMLButtonElement>("#refreshJobs").addEventListener("click", () => void loadJobs());
requiredElement<HTMLButtonElement>("#logoutButton").addEventListener("click", async () => {
  await api("/auth/logout", { method: "POST" }).catch(() => null);
  authenticated = false;
  mainView.hidden = true;
  loginView.hidden = false;
});

function updateNetworkStatus(): void {
  offlineBanner.hidden = navigator.onLine;
}

window.addEventListener("online", () => {
  updateNetworkStatus();
  if (authenticated) void loadJobs();
});
window.addEventListener("offline", updateNetworkStatus);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "visible" && authenticated) void loadJobs();
});

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js");
  });
}

updateNetworkStatus();
void loadJobs();
window.setInterval(() => {
  if (authenticated && document.visibilityState === "visible") void loadJobs();
}, 15_000);
